import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const NumeroSolicitudes = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("todas");
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  // Checkboxes de tipo de solicitudes (los 3 seleccionados por defecto)
  const [chkCanceladas, setChkCanceladas] = useState(true);
  const [chkPagadas, setChkPagadas] = useState(true);
  const [chkPorPagar, setChkPorPagar] = useState(true);

  const [hoveredBar, setHoveredBar] = useState(null);

  useEffect(() => {
    document.title = "Número de Solicitudes";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      setSolicitudes(listaSol);
      setSucursales(listaSuc);
    } catch (error) {
      console.error("Error al cargar datos iniciales:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron obtener los datos para el reporte.",
        icon: "error",
        confirmButtonColor: "#006787",
      });
    } finally {
      setLoading(false);
    }
  };

  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }
    return null;
  };

  // Clasificación de la solicitud según estatus del sistema (C=Cancelada, P=Pagada, PC=Parcialmente pagada, S=Sin pagos)
  const isCancelada = (sol) => {
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    return est === "C" || est === "CANCELADA" || Boolean(sol.motivoCancelacion && String(sol.motivoCancelacion).trim() !== "");
  };

  const isPagada = (sol) => {
    const est = `${sol.estatus ?? ""}`.trim().toUpperCase();
    return est === "P" || est === "PAGADA" || est === "LIQUIDADA";
  };

  const isPorPagar = (sol) => {
    if (isCancelada(sol)) return false;
    if (isPagada(sol)) return false;
    return true; // Todas las demás: PC (Parcialmente pagada), S (Sin pagos), etc.
  };

  // Filtrado de solicitudes según los controles
  const solicitudesFiltradas = useMemo(() => {
    const deDate = parseFechaDate(fechaDe <= fechaA ? fechaDe : fechaA);
    const aDate = parseFechaDate(fechaDe <= fechaA ? fechaA : fechaDe);

    return solicitudes.filter((sol) => {
      // Filtro de sucursal
      if (sucursalSeleccionada !== "todas") {
        const sucId = sol.idSucursal || sol.id_sucursal;
        if (String(sucId) !== String(sucursalSeleccionada)) {
          return false;
        }
      }

      // Filtro de rango de fechas
      if (deDate || aDate) {
        const fechaSol = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.createdAt);
        if (fechaSol) {
          if (deDate && fechaSol < deDate) return false;
          if (aDate && fechaSol > aDate) return false;
        }
      }

      // Filtro de tipos de solicitud (Checkboxes)
      const cancel = isCancelada(sol);
      const porPagar = isPorPagar(sol);
      const pagada = isPagada(sol);

      let pasaTipo = false;
      if (chkCanceladas && cancel) pasaTipo = true;
      if (chkPagadas && pagada) pasaTipo = true;
      if (chkPorPagar && porPagar) pasaTipo = true;

      return pasaTipo;
    });
  }, [solicitudes, sucursalSeleccionada, fechaDe, fechaA, chkCanceladas, chkPagadas, chkPorPagar]);

  // Lista de sucursales visibles en la tabla/gráfica
  const sucursalesVisibles = useMemo(() => {
    if (sucursalSeleccionada === "todas") {
      return sucursales;
    }
    return sucursales.filter(
      (s) => String(s.idSucursal || s.id) === String(sucursalSeleccionada)
    );
  }, [sucursales, sucursalSeleccionada]);

  // --- DATOS TABLA: Cantidad de solicitudes por Sucursal ---
  const datosTabla = useMemo(() => {
    return sucursalesVisibles.map((suc) => {
      const sucId = suc.idSucursal || suc.id;
      const sucNombre = suc.nombre || suc.nombreSucursal || `Sucursal ${sucId}`;

      const solsSuc = solicitudesFiltradas.filter(
        (sol) => String(sol.idSucursal || sol.id_sucursal) === String(sucId)
      );

      return {
        idSucursal: sucId,
        sucursal: sucNombre,
        fechaInicial: fechaDe,
        fechaFinal: fechaA,
        cantidadSolicitudes: solsSuc.length,
      };
    });
  }, [sucursalesVisibles, solicitudesFiltradas, fechaDe, fechaA]);

  // Total Acumulado de la Tabla
  const totalCantidadSolicitudes = useMemo(() => {
    return datosTabla.reduce((acc, row) => acc + row.cantidadSolicitudes, 0);
  }, [datosTabla]);

  // --- CÁLCULO DINÁMICO DE LA ESCALA EN EJE Y (GRÁFICA) ---
  const configuracionGraficaY = useMemo(() => {
    let max = 0;
    datosTabla.forEach((d) => {
      if (d.cantidadSolicitudes > max) max = d.cantidadSolicitudes;
    });

    let step = 10;
    let roundedMax = 10;

    if (max <= 10) {
      step = max <= 5 ? 1 : 2;
      roundedMax = max <= 5 ? 5 : 10;
    } else if (max <= 100) {
      step = 10;
      roundedMax = Math.ceil(max / 10) * 10;
    } else if (max <= 1000) {
      step = 100;
      roundedMax = Math.ceil(max / 100) * 100;
    } else {
      step = 1000;
      if (max > 10000) {
        const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
        step = magnitude / 2;
      }
      roundedMax = Math.ceil(max / step) * step;
    }

    const ticks = [];
    for (let v = roundedMax; v >= 0; v -= step) {
      ticks.push(v);
    }

    return { max, step, roundedMax, ticks };
  }, [datosTabla]);

  // --- EXPORTAR A PDF ---
  const handleExportarPDF = async () => {
    if (datosTabla.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen registros para exportar en el rango seleccionado.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF("portrait", "mm", "a4");
        const startY = 12;

    let sucursalActualObj = sucursales[0] || {};
    const sucursalNomText = (sucursalActualObj.nombre || "LABORATORIO DE ANÁLISIS CLÍNICOS").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 24);
    } catch (e) {}

    // Recuadro "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Encabezado de la Sucursal en la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });

    // Título principal
    currY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(24, 35, 79);
    doc.text("REPORTE DE NÚMERO DE SOLICITUDES", 14, currY);

    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Período del ${fechaDe} al ${fechaA}`, 14, currY + 5);

    let nextY = currY + 12;

    // --- TABLA EN PDF ---
    const bodyPDF = datosTabla.map((row) => [
      row.sucursal,
      row.fechaInicial,
      row.fechaFinal,
      row.cantidadSolicitudes.toLocaleString(),
    ]);

    // Fila Total Acumulado
    bodyPDF.push([
      "TOTAL ACUMULADO",
      fechaDe,
      fechaA,
      totalCantidadSolicitudes.toLocaleString(),
    ]);

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          { content: "Sucursal", styles: { halign: "left" } },
          { content: "Fecha inicial", styles: { halign: "center" } },
          { content: "Fecha final", styles: { halign: "center" } },
          { content: "Cantidad de solicitudes", styles: { halign: "center" } },
        ],
      ],
      body: bodyPDF,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
      styles: { fontSize: 8.5, cellPadding: 2.5, halign: "center" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Numero_Solicitudes_${fechaDe}_al_${fechaA}.pdf`);
      },
      "Generando reporte de Número de Solicitudes en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal al estilo de los demás informes */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE DE NÚMERO DE SOLICITUDES
                </Typography>
              </Box>

              {/* Filtros del Reporte */}
              <Box sx={{ width: "95%", maxWidth: "1200px", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "10px 14px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "1.05rem" }}>
                        Filtros del Reporte
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila Sucursal */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                          sx={{ width: "320px" }}
                        >
                          <MenuItem value="todas">Todas las sucursales</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal || suc.id} value={String(suc.idSucursal || suc.id)}>
                              {suc.nombre || suc.nombreSucursal}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila Tipo de solicitudes */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Tipo de solicitudes:
                      </TableCell>
                      <TableCell>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkCanceladas}
                                onChange={(e) => setChkCanceladas(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Órdenes canceladas</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkPagadas}
                                onChange={(e) => setChkPagadas(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Órdenes pagadas</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={chkPorPagar}
                                onChange={(e) => setChkPorPagar(e.target.checked)}
                                size="small"
                                sx={{ color: "#006787", "&.Mui-checked": { color: "#006787" } }}
                              />
                            }
                            label={<Typography variant="body2">Órdenes por pagar</Typography>}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Fila Rango de Fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Seleccione un rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <TextField
                            label="De:"
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={(e) => setFechaDe(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                          <TextField
                            label="A:"
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={(e) => setFechaA(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Subtítulo indicando el rango de fechas */}
              <Typography
                variant="subtitle1"
                align="center"
                sx={{ fontWeight: "bold", color: "#18234f", mb: 3 }}
              >
                Número de solicitudes del {fechaDe} al {fechaA}
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={50} sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Stack spacing={4} sx={{ width: "100%" }}>
                  {/* --- SECCIÓN DE LA GRÁFICA --- */}
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 3, backgroundColor: "#ffffff" }}>
                    <Typography
                      variant="h6"
                      align="center"
                      fontWeight="bold"
                      sx={{ color: "#006787", mb: 3, fontFamily: "Roboto-Medium" }}
                    >
                      Cantidad de Solicitudes
                    </Typography>

                    {/* Renderizado de la Gráfica de Barras SVG con escala dinámica en Eje Y */}
                    <Box sx={{ width: "100%", overflowX: "auto", py: 1 }}>
                      <Box sx={{ minWidth: 600, height: 380, position: "relative" }}>
                        <svg width="100%" height="100%" viewBox="0 0 800 350" preserveAspectRatio="none">
                          {/* Eje Y: Líneas de cuadrícula e indicadores dinámicos */}
                          {configuracionGraficaY.ticks.map((tickVal) => {
                            const yPos = 300 - (tickVal / configuracionGraficaY.roundedMax) * 260;
                            return (
                              <g key={tickVal}>
                                <line
                                  x1="65"
                                  y1={yPos}
                                  x2="780"
                                  y2={yPos}
                                  stroke="#e2e8f0"
                                  strokeDasharray={tickVal === 0 ? "none" : "3 3"}
                                  strokeWidth={tickVal === 0 ? "2" : "1"}
                                />
                                <text
                                  x="58"
                                  y={yPos + 4}
                                  fill="#64748b"
                                  fontSize="11"
                                  fontWeight="bold"
                                  textAnchor="end"
                                >
                                  {tickVal.toLocaleString()}
                                </text>
                              </g>
                            );
                          })}

                          {/* Eje vertical Y principal */}
                          <line x1="65" y1="30" x2="65" y2="300" stroke="#94a3b8" strokeWidth="2" />
                          <text
                            x="20"
                            y="160"
                            fill="#475569"
                            fontSize="11"
                            fontWeight="bold"
                            transform="rotate(-90 20 160)"
                            textAnchor="middle"
                          >
                            Cantidad de Solicitudes
                          </text>

                          {/* Eje horizontal X */}
                          <line x1="65" y1="300" x2="780" y2="300" stroke="#94a3b8" strokeWidth="2" />

                          {/* Render de Barras por Sucursal */}
                          {datosTabla.map((d, index) => {
                            const totalGrupos = datosTabla.length;
                            const availableWidth = 715;
                            const groupWidth = availableWidth / Math.max(totalGrupos, 1);
                            const groupX = 65 + index * groupWidth;
                            const barWidth = Math.min(48, groupWidth - 24);

                            const hBar = (d.cantidadSolicitudes / configuracionGraficaY.roundedMax) * 260;
                            const yBar = 300 - hBar;
                            const xBar = groupX + groupWidth / 2 - barWidth / 2;

                            return (
                              <g key={d.idSucursal}>
                                <rect
                                  x={xBar}
                                  y={yBar}
                                  width={barWidth}
                                  height={hBar}
                                  fill="#006787"
                                  rx="4"
                                  ry="4"
                                  style={{ transition: "all 0.3s ease", cursor: "pointer" }}
                                  onMouseEnter={() =>
                                    setHoveredBar({
                                      sucursal: d.sucursal,
                                      solicitudes: d.cantidadSolicitudes,
                                    })
                                  }
                                  onMouseLeave={() => setHoveredBar(null)}
                                />
                                {d.cantidadSolicitudes > 0 && (
                                  <text
                                    x={xBar + barWidth / 2}
                                    y={yBar - 6}
                                    fill="#006787"
                                    fontSize="11"
                                    fontWeight="bold"
                                    textAnchor="middle"
                                  >
                                    {d.cantidadSolicitudes}
                                  </text>
                                )}

                                {/* Nombre de la Sucursal en Eje X */}
                                <text
                                  x={groupX + groupWidth / 2}
                                  y="322"
                                  fill="#334155"
                                  fontSize="11"
                                  fontWeight="bold"
                                  textAnchor="middle"
                                >
                                  {d.sucursal.length > 18
                                    ? `${d.sucursal.substring(0, 16)}...`
                                    : d.sucursal}
                                </text>
                              </g>
                            );
                          })}
                        </svg>

                        {/* Tooltip flotante */}
                        {hoveredBar && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 20,
                              backgroundColor: "rgba(15, 23, 42, 0.85)",
                              color: "white",
                              p: 1.2,
                              borderRadius: 2,
                              fontSize: "0.85rem",
                              pointerEvents: "none",
                              boxShadow: 3,
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {hoveredBar.sucursal}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#38bdf8" }}>
                              Solicitudes: <strong>{hoveredBar.solicitudes}</strong>
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>

                  {/* --- TABLA: Detalle por Sucursal --- */}
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 3, backgroundColor: "#ffffff" }}>
                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ border: "1px solid #e2e8f0" }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#006787" }}>
                            <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Sucursal
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Fecha inicial
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Fecha final
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Cantidad de solicitudes
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {datosTabla.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 3, color: "#64748b" }}>
                                No existen solicitudes registradas para el filtro seleccionado.
                              </TableCell>
                            </TableRow>
                          ) : (
                            datosTabla.map((row) => (
                              <TableRow key={row.idSucursal} sx={{ "&:hover": { backgroundColor: "#f1f5f9" } }}>
                                <TableCell sx={{ fontWeight: "bold", color: "#1e293b" }}>{row.sucursal}</TableCell>
                                <TableCell align="center">{row.fechaInicial}</TableCell>
                                <TableCell align="center">{row.fechaFinal}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787" }}>
                                  {row.cantidadSolicitudes.toLocaleString()}
                                </TableCell>
                              </TableRow>
                            ))
                          )}

                          {/* Fila Total Acumulado */}
                          {datosTabla.length > 0 && (
                            <TableRow sx={{ backgroundColor: "#f8fafc", borderTop: "2px solid #006787" }}>
                              <TableCell sx={{ fontWeight: "bold", color: "#006787", fontSize: "0.95rem" }}>
                                TOTAL ACUMULADO
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {fechaDe}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {fechaA}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#006787", fontSize: "1rem" }}>
                                {totalCantidadSolicitudes.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón flotante lateral derecho para exportar PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default NumeroSolicitudes;
